import { useEffect, useRef, useState } from "react";
import AirportWeather from "./AirportWeather.jsx";
import AtisText from "./AtisText.jsx";
import { liveatcUrl } from "../data/airports.js";
import "./AirportPanel.css";

// A live ATC player + weather for one airport. Streams audio through
// /api/atc/:mount (our proxy past LiveATC's hot-link protection).
export default function AirportPanel({ airport, onClose, onViewDetail }) {
  const audioRef = useRef(null);
  const feeds = airport?.atc || [];
  const [feed, setFeed] = useState(feeds[0] || null);
  const [state, setState] = useState("idle"); // idle | loading | playing | error
  const [volume, setVolume] = useState(0.9);

  // Reset when the airport changes.
  useEffect(() => {
    setFeed((airport?.atc || [])[0] || null);
    setState("idle");
  }, [airport]);

  // Point the audio element at the selected feed and try to play. Browsers may
  // refuse autoplay outside a click; if so we drop back to "idle" so the Listen
  // button (a direct gesture) can start it reliably.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !feed) return;
    el.src = `/api/atc/${feed.mount}`;
    el.load();
    setState("loading");
    el.play().catch(() => setState("idle"));
    return () => {
      el.pause();
      el.removeAttribute("src");
      el.load();
    };
  }, [feed]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Play/pause toggle — always driven by a real click, so it isn't blocked.
  function togglePlay() {
    const el = audioRef.current;
    if (!el || !feed) return;
    if (el.paused) {
      if (!el.getAttribute("src")) {
        el.src = `/api/atc/${feed.mount}`;
        el.load();
      }
      setState("loading");
      el.play().catch(() => setState("error"));
    } else {
      el.pause();
      setState("idle");
    }
  }

  if (!airport) return null;

  const isLive = state === "playing" || state === "loading";

  return (
    <aside className="details airport-panel" aria-label={`Live ATC for ${airport.name}`}>
      <div className="details-head">
        <div>
          <h2>{airport.name}</h2>
          <p className="subtitle muted">
            {airport.icao}
            {airport.iata ? ` · ${airport.iata}` : ""} — live air traffic control
          </p>
        </div>
        <button className="close" onClick={onClose} aria-label="Close airport">
          ✕
        </button>
      </div>

      <div className="atc-player">
        <div className="atc-status" role="status" aria-live="polite">
          <span className={`atc-dot ${state}`} aria-hidden="true" />
          {state === "playing" && `On air · ${feed?.label || "ATC"}`}
          {state === "loading" && "Connecting to the tower…"}
          {state === "idle" && "Pick a channel to listen"}
          {state === "error" && "That channel is quiet or offline right now"}
        </div>

        {feeds.length > 0 ? (
          <>
            <button
              className={isLive ? "atc-play live" : "atc-play"}
              onClick={togglePlay}
              aria-pressed={isLive}
            >
              {state === "playing" ? "⏸ Pause" : state === "loading" ? "⏳ Connecting…" : "▶ Listen live"}
            </button>

            <div className="atc-feeds" role="group" aria-label="Frequencies">
              {feeds.map((f) => (
                <button
                  key={f.mount}
                  className={feed?.mount === f.mount ? "atc-feed active" : "atc-feed"}
                  aria-pressed={feed?.mount === f.mount}
                  onClick={() => setFeed(f)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <label className="atc-volume">
              🔊
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                aria-label="Volume"
              />
            </label>
          </>
        ) : (
          <p className="muted small">
            No built-in feed for this airport yet — browse its channels on LiveATC below.
          </p>
        )}

        <audio
          ref={audioRef}
          onPlaying={() => setState("playing")}
          onWaiting={() => setState("loading")}
          onError={() => setState("error")}
          preload="none"
        />
      </div>

      <button className="rv-open-btn" onClick={() => onViewDetail?.(airport)}>
        🛬 View airport &amp; runways in detail
      </button>

      <AtisText icao={airport.icao} />

      <AirportWeather icao={airport.icao} label="Weather at the field" />

      <a className="atc-link" href={liveatcUrl(airport.icao)} target="_blank" rel="noreferrer">
        Browse all feeds on LiveATC ↗
      </a>

      <p className="muted small">
        Audio: LiveATC.net volunteer receivers. Feeds can lag ~30s and go quiet when
        the field is idle — that's real, not a bug.
      </p>
    </aside>
  );
}
